import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { cn } from '../lib/utils';
import PaymentReceipt from './PaymentReceipt';
import {
  Package as PackageIcon, Plus, Check, CheckCircle2, IndianRupee, Trash2,
  Pencil, History as HistoryIcon, RefreshCw, ChevronsUpDown, X as XIcon,
} from 'lucide-react';

/**
 * PackagePOS – manages a patient's service packages inside the Teller POS.
 * Props:
 *   patientId      – required; the patient the teller is transacting for
 *   patientName    – for display only
 *   services       – active services list (fetched by parent)
 *   paymentMethods – active payment methods (fetched by parent)
 *   userRole       – 'Admin' | 'Teller' | ...
 *   onChanged      – optional callback invoked after any successful write
 */
const PackagePOS = ({ patientId, patientName = '', services = [], paymentMethods = [], counsellors = [], userRole = 'Admin', pendingAppointments = [], selectedAppointment = null, onChanged, onRedeemed }) => {
  const [receiptPayment, setReceiptPayment] = useState(null);
  const canWrite = userRole === 'Admin' || userRole === 'Teller';

  // patientAppts is defined further down, AFTER redeemAppts is declared,
  // to avoid a temporal-dead-zone ReferenceError.

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  // ==== Builder ====
  const [builderOpen, setBuilderOpen] = useState(false);
  const [newItems, setNewItems] = useState([]); // [{service_id, service_name, quantity_total}]
  const [newService, setNewService] = useState(null);
  const [newQty, setNewQty] = useState('1');
  const [totalCost, setTotalCost] = useState('');
  const [initPayment, setInitPayment] = useState('');
  const [initMethod, setInitMethod] = useState(null);
  const [initCounsellor, setInitCounsellor] = useState(null);
  const [expiry, setExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ==== Consume ====
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeTarget, setConsumeTarget] = useState(null); // package
  const [consumeService, setConsumeService] = useState(null); // {service_id, service_name}
  const [consumeAppointment, setConsumeAppointment] = useState(null);
  const [consumeSubmitting, setConsumeSubmitting] = useState(false);

  // ==== Pay Balance ====
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState(null);
  const [payCounsellor, setPayCounsellor] = useState(null);
  const [paySubmitting, setPaySubmitting] = useState(false);

  // ==== Edit ====
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editTotalCost, setEditTotalCost] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editAddService, setEditAddService] = useState(null);
  const [editAddQty, setEditAddQty] = useState('1');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // ==== History ====
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ==== Redemption-eligible appointments ====
  // Any pending (non-terminal, unpaid) appointment for the patient regardless
  // of date — because a package credit can be redeemed against a future or
  // today's appointment, not just today's list. Kept in local state and
  // refreshed alongside packages.
  const [redeemAppts, setRedeemAppts] = useState([]);

  // Only pending / eligible appointments for THIS patient. Prefer the locally
  // loaded `redeemAppts` (all dates, unpaid, non-terminal) — the parent's
  // `pendingAppointments` is only today's list, which caused the "Use" button
  // to stay disabled whenever the appointment was booked for a future date.
  const patientAppts = useMemo(() => {
    if (redeemAppts.length > 0) return redeemAppts;
    const ok = new Set(['scheduled', 'confirmed', 'checked_in', 'checked_out']);
    return (pendingAppointments || []).filter(a =>
      a && a.patient_id === patientId
        && ok.has(String(a.status || '').toLowerCase())
        && !a.is_paid
    );
  }, [redeemAppts, pendingAppointments, patientId]);

  const loadPackages = useCallback(async () => {
    if (!patientId) { setPackages([]); setRedeemAppts([]); return; }
    setLoading(true);

    // 1) Packages — the primary payload, must never be blocked by the
    //    appointment fetch (which is optional context for the Use button).
    try {
      const pkgRes = await api.get('/packages', { params: { patient_id: patientId, status: 'active', limit: 50 } });
      setPackages(Array.isArray(pkgRes.data?.rows) ? pkgRes.data.rows : []);
    } catch (e) {
      console.error('[PackagePOS] packages fetch failed', e);
      setPackages([]);
      toast.error('Failed to load packages');
    }

    // 2) Appointments — soft-fail. If this errors, fall back to the parent's
    //    pendingAppointments list; the redemption filter tolerates either.
    try {
      const apptRes = await api.get(`/patients/${patientId}/appointments`);
      const OK_STATUS = new Set(['scheduled', 'confirmed', 'checked_in', 'checked_out']);
      const raw = Array.isArray(apptRes.data) ? apptRes.data : [];
      const rows = raw.filter(a =>
        a && OK_STATUS.has(String(a.status || '').toLowerCase()) && !a.is_paid
      );
      setRedeemAppts(rows);
    } catch (e) {
      console.error('[PackagePOS] appointments fetch failed — falling back to parent list', e);
      setRedeemAppts([]);
    }

    setLoading(false);
  }, [patientId]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const resetBuilder = () => {
    setNewItems([]); setNewService(null); setNewQty('1');
    setTotalCost(''); setInitPayment(''); setInitMethod(null); setInitCounsellor(null);
    setExpiry(''); setNotes('');
  };

  const addBuilderItem = () => {
    if (!newService) { toast.error('Pick a service'); return; }
    if (newItems.find(x => x.service_id === newService.id)) {
      toast.error('Already added — remove first if you want to change quantity'); return;
    }
    const q = parseInt(newQty, 10);
    if (!(q > 0)) { toast.error('Quantity must be at least 1'); return; }
    setNewItems([...newItems, { service_id: newService.id, service_name: newService.name, quantity_total: q }]);
    setNewService(null); setNewQty('1');
  };

  const submitCreate = async () => {
    // Auto-add pending service if user forgot to press "+"
    let itemsToUse = newItems;
    if (itemsToUse.length === 0 && newService) {
      const q = parseInt(newQty, 10);
      if (!(q > 0)) { toast.error('Quantity must be at least 1'); return; }
      itemsToUse = [{ service_id: newService.id, service_name: newService.name, quantity_total: q }];
      setNewItems(itemsToUse);
      setNewService(null); setNewQty('1');
    }
    if (itemsToUse.length === 0) { toast.error('Pick a service and click + to add it'); return; }
    const tc = parseFloat(totalCost);
    if (!(tc >= 0)) { toast.error('Enter a valid total cost'); return; }
    const ip = parseFloat(initPayment || '0');
    if (ip > 0 && !initMethod) { toast.error('Pick a payment method for the initial payment'); return; }
    if (ip > 0 && !initCounsellor) { toast.error('Pick a counsellor to credit for the initial payment (incentive)'); return; }
    if (ip > tc) { toast.error('Initial payment exceeds total cost'); return; }
    try {
      setSubmitting(true);
      const payload = {
        patient_id: patientId,
        items: itemsToUse.map(it => ({ service_id: it.service_id, quantity_total: it.quantity_total })),
        total_cost: tc,
        initial_payment: ip,
        payment_method_id: initMethod?.id,
        payment_method_name: initMethod?.name,
        counsellor_id: initCounsellor?.id,
        expiry_date: expiry || null,
        notes: notes || null,
      };
      const res = await api.post('/packages', payload);
      const pts = Number(res.data?.reward_points_earned || 0);
      toast.success(pts > 0
        ? `Package created · +${pts} reward points earned`
        : 'Package created');
      setBuilderOpen(false);
      resetBuilder();
      if (res.data?.receipt) setReceiptPayment(res.data.receipt);
      await loadPackages();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create package');
    } finally {
      setSubmitting(false);
    }
  };

  const openConsume = (pkg) => {
    setConsumeTarget(pkg);
    setConsumeService(null);
    // When embedded inside the Teller payment modal for a specific appointment,
    // lock the consumption target to THAT appointment. Prevents redeeming a
    // credit against a different pending appointment for the same patient
    // (which used to leave the parent modal open + the original appointment
    // stuck as unpaid).
    setConsumeAppointment(selectedAppointment || null);
    setConsumeOpen(true);
  };

  const submitConsume = async () => {
    if (!consumeTarget || !consumeService) { toast.error('Pick a service'); return; }
    if (!consumeAppointment) {
      toast.error('Pick a pending appointment to redeem against');
      return;
    }
    try {
      setConsumeSubmitting(true);
      const consumeRes = await api.post(`/packages/${consumeTarget.id}/consume`, {
        service_id: consumeService.service_id,
        appointment_id: consumeAppointment.id,
      });
      toast.success(`1 credit used from ${consumeTarget.ref_number}`);
      setConsumeOpen(false);
      if (consumeRes.data?.receipt) setReceiptPayment(consumeRes.data.receipt);
      await loadPackages();
      onChanged && onChanged();
      onRedeemed && onRedeemed(consumeAppointment);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to consume credit');
    } finally {
      setConsumeSubmitting(false);
    }
  };

  const openPay = (pkg) => {
    setPayTarget(pkg);
    setPayAmount(String(Math.max(0, Number(pkg.balance || 0))));
    setPayMethod(paymentMethods[0] || null);
    setPayCounsellor(null);
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!payTarget) return;
    const amt = parseFloat(payAmount);
    if (!(amt > 0)) { toast.error('Enter a valid amount'); return; }
    if (!payMethod) { toast.error('Pick a payment method'); return; }
    if (!payCounsellor) { toast.error('Pick a counsellor to credit (incentive)'); return; }
    try {
      setPaySubmitting(true);
      const res = await api.post(`/packages/${payTarget.id}/pay-balance`, {
        amount: amt,
        payment_method_id: payMethod.id,
        payment_method_name: payMethod.name,
        counsellor_id: payCounsellor.id,
      });
      const pts = Number(res.data?.reward_points_earned || 0);
      toast.success(pts > 0
        ? `Balance received · +${pts} reward points earned`
        : 'Balance received');
      setPayOpen(false);
      if (res.data?.receipt) setReceiptPayment(res.data.receipt);
      await loadPackages();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaySubmitting(false);
    }
  };

  const openEdit = (pkg) => {
    setEditTarget(pkg);
    setEditItems((pkg.items || []).map(it => ({
      service_id: it.service_id,
      service_name: it.service_name,
      quantity_total: Number(it.quantity_total || 0),
      quantity_consumed: Number(it.quantity_consumed || 0),
    })));
    setEditTotalCost(String(pkg.total_cost ?? ''));
    setEditExpiry(pkg.expiry_date || '');
    setEditNotes(pkg.notes || '');
    setEditReason('');
    setEditAddService(null);
    setEditAddQty('1');
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    if (editItems.length === 0) { toast.error('Must contain at least one service'); return; }
    const tc = parseFloat(editTotalCost);
    if (!(tc >= 0)) { toast.error('Invalid total cost'); return; }
    if (tc < Number(editTarget.total_paid || 0)) {
      toast.error(`Total cost cannot be less than paid (₹${editTarget.total_paid})`);
      return;
    }
    for (const it of editItems) {
      if (it.quantity_total < it.quantity_consumed) {
        toast.error(`${it.service_name}: qty ${it.quantity_total} < consumed ${it.quantity_consumed}`);
        return;
      }
    }
    try {
      setEditSubmitting(true);
      await api.put(`/packages/${editTarget.id}`, {
        items: editItems.map(it => ({ service_id: it.service_id, quantity_total: it.quantity_total })),
        total_cost: tc,
        expiry_date: editExpiry || null,
        notes: editNotes || null,
        reason: editReason || null,
      });
      toast.success('Package updated');
      setEditOpen(false);
      await loadPackages();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openHistory = async (pkg) => {
    setHistoryTarget(pkg);
    setHistoryOpen(true);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/packages/${pkg.id}/versions`);
      setHistoryRows(res.data?.versions || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  if (!patientId) return null;

  return (
    <div className="border border-purple-200 bg-purple-50/40 rounded-xl p-3 mb-3" data-testid="package-pos-panel">
      <PaymentReceipt payment={receiptPayment} open={!!receiptPayment} onClose={() => setReceiptPayment(null)} />
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <PackageIcon className="w-4 h-4 text-purple-600" />
          <p className="text-sm font-semibold text-gray-800">Packages</p>
          {loading && <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />}
        </div>
      </div>

      {canWrite && (
        <Button
          onClick={() => { resetBuilder(); setBuilderOpen(true); }}
          data-testid="pos-pkg-create-btn"
          className="w-full mb-2 bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Create New Package
        </Button>
      )}

      {!loading && packages.length === 0 && (
        <p className="text-xs text-gray-500 italic text-center py-1" data-testid="pos-pkg-empty">
          No active packages for this patient yet.
        </p>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {packages.map(pkg => {
          const remaining = (pkg.items || []).reduce((s, it) => s + (Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)), 0);
          const bal = Number(pkg.balance || 0);
          // Match by service_id first; fall back to case-insensitive service_name
          // so a package still redeems even if the two records share only the
          // name (e.g. the appointment was booked against an older service_id
          // than the one now on the package item).
          const pkgSvcIds = new Set((pkg.items || []).map(it => it.service_id).filter(Boolean));
          const pkgSvcNames = new Set(
            (pkg.items || [])
              .map(it => String(it.service_name || '').trim().toLowerCase())
              .filter(Boolean)
          );
          const eligibleAppts = patientAppts.filter(a => {
            if (a.service_id && pkgSvcIds.has(a.service_id)) return true;
            const n = String(a.service_name || '').trim().toLowerCase();
            return n && pkgSvcNames.has(n);
          });
          const useDisabled = remaining <= 0 || eligibleAppts.length === 0;
          return (
            <div key={pkg.id} className="bg-white border border-purple-200 rounded-lg p-2.5" data-testid={`pos-pkg-card-${pkg.ref_number}`}>
              <div className="flex items-start justify-between mb-1">
                <div>
                  <span className="font-mono text-[10px] text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">{pkg.ref_number}</span>
                  <span className="ml-1.5 text-[11px] text-gray-500">
                    ₹{Number(pkg.total_paid).toLocaleString()}/{Number(pkg.total_cost).toLocaleString()}
                    {bal > 0 && <span className="text-orange-600 font-medium"> · Bal ₹{bal.toLocaleString()}</span>}
                  </span>
                </div>
                <Badge className="bg-purple-100 text-purple-700 text-[10px]">{remaining} left</Badge>
              </div>
              <div className="text-[11px] text-gray-600 mb-2 space-y-0.5">
                {(pkg.items || []).map(it => (
                  <div key={it.service_id} className="flex justify-between">
                    <span className="truncate pr-1">{it.service_name}</span>
                    <span className="font-mono">{it.quantity_consumed || 0}/{it.quantity_total}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  disabled={useDisabled}
                  onClick={() => openConsume(pkg)}
                  data-testid={`pos-pkg-consume-${pkg.ref_number}`}
                  title={remaining <= 0
                    ? 'No credits left'
                    : eligibleAppts.length === 0
                      ? 'No pending appointment for any service in this package. Create an appointment first.'
                      : 'Redeem 1 credit'}
                  className="flex-1 h-7 text-xs bg-purple-600 hover:bg-purple-700 disabled:opacity-40"
                >
                  <Check className="w-3 h-3 mr-1" /> Use
                </Button>
                {canWrite && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bal <= 0}
                    onClick={() => openPay(pkg)}
                    data-testid={`pos-pkg-pay-balance-${pkg.ref_number}`}
                    className="flex-1 h-7 text-xs border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-40"
                  >
                    <IndianRupee className="w-3 h-3 mr-1" /> Pay Bal
                  </Button>
                )}
              </div>
              {useDisabled && (
                <p
                  className="text-[10.5px] text-rose-600 mt-1 leading-snug"
                  data-testid={`pos-pkg-use-hint-${pkg.ref_number}`}
                >
                  {remaining <= 0
                    ? 'No credits left in this package.'
                    : 'Book an appointment for a service in this package before you can redeem a credit.'}
                </p>
              )}
              {canWrite && (
                <div className="flex gap-1.5 mt-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(pkg)}
                    data-testid={`pos-pkg-edit-${pkg.ref_number}`}
                    className="flex-1 h-6 text-[11px] text-purple-700 hover:bg-purple-100"
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openHistory(pkg)}
                    data-testid={`pos-pkg-history-${pkg.ref_number}`}
                    className="flex-1 h-6 text-[11px] text-gray-600 hover:bg-gray-100"
                  >
                    <HistoryIcon className="w-3 h-3 mr-1" /> History
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ============ Builder Dialog ============ */}
      <Dialog open={builderOpen} onOpenChange={(o) => { if (!o) { setBuilderOpen(false); resetBuilder(); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pkg-builder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageIcon className="w-5 h-5 text-purple-600" /> Create Package
            </DialogTitle>
            <DialogDescription>For {patientName || 'patient'}. Pick services, set quantities, price and (optionally) collect an initial payment.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Services</Label>
              <div className="mt-1 space-y-1.5">
                {newItems.map(it => (
                  <div key={it.service_id} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded px-2 py-1.5 text-sm">
                    <span className="flex-1 truncate">{it.service_name}</span>
                    <span className="text-purple-700 font-mono text-xs">x{it.quantity_total}</span>
                    <button
                      type="button"
                      onClick={() => setNewItems(items => items.filter(x => x.service_id !== it.service_id))}
                      className="p-0.5 hover:bg-purple-100 rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-purple-600" />
                    </button>
                  </div>
                ))}
                {newItems.length === 0 && <p className="text-xs text-amber-600 italic">Pick a service, set quantity, then click <span className="font-semibold">+</span> to add it.</p>}
              </div>
              <div className="flex gap-2 mt-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal h-9" data-testid="pkg-builder-service-select">
                      {newService ? newService.name : <span className="text-gray-500">Pick service…</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search services..." />
                      <CommandList>
                        <CommandEmpty>No service found.</CommandEmpty>
                        <CommandGroup>
                          {[...services].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                            <CommandItem key={s.id} value={s.name} onSelect={() => setNewService(s)}>
                              <Check className={cn('mr-2 h-4 w-4', newService?.id === s.id ? 'opacity-100' : 'opacity-0')} />
                              {s.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Input type="number" min="1" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="w-16 h-9" data-testid="pkg-builder-qty" />
                <Button type="button" onClick={addBuilderItem} className="bg-purple-600 hover:bg-purple-700 h-9" data-testid="pkg-builder-add">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Total package cost</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="number" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} className="pl-9 font-semibold" data-testid="pkg-builder-total-cost" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Initial payment (optional)</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="number" value={initPayment} onChange={(e) => setInitPayment(e.target.value)} className="pl-9" data-testid="pkg-builder-init-payment" />
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-32 justify-between font-normal" data-testid="pkg-builder-init-method">
                      {initMethod ? initMethod.name : <span className="text-gray-500">Method</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0">
                    <Command>
                      <CommandList>
                        <CommandGroup>
                          {paymentMethods.map(m => (
                            <CommandItem key={m.id} value={m.name} onSelect={() => setInitMethod(m)}>{m.name}</CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {parseFloat(initPayment || '0') > 0 && (
                <div className="mt-2">
                  <Label className="text-[11px] text-gray-500">Credit to (for incentive)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between font-normal mt-1" data-testid="pkg-builder-init-counsellor">
                        {initCounsellor ? initCounsellor.name : <span className="text-gray-500">Pick counsellor…</span>}
                        <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                      <Command>
                        <CommandInput placeholder="Search counsellor..." />
                        <CommandList>
                          <CommandEmpty>No counsellor found.</CommandEmpty>
                          <CommandGroup>
                            {counsellors.map(c => (
                              <CommandItem key={c.id} value={c.name} onSelect={() => setInitCounsellor(c)}>
                                <Check className={cn('mr-2 h-4 w-4', initCounsellor?.id === c.id ? 'opacity-100' : 'opacity-0')} />
                                {c.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold">Expiry date (optional)</Label>
              <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" data-testid="pkg-builder-expiry" />
            </div>

            <div>
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="pkg-builder-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setBuilderOpen(false); resetBuilder(); }}>Cancel</Button>
            <Button
              onClick={submitCreate}
              disabled={submitting || (newItems.length === 0 && !newService)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="pkg-builder-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {submitting ? 'Creating…' : 'Create Package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Consume Dialog ============ */}
      <Dialog open={consumeOpen} onOpenChange={(o) => { if (!o) { setConsumeOpen(false); setConsumeTarget(null); setConsumeService(null); setConsumeAppointment(null); } }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="pkg-consume-dialog">
          <DialogHeader>
            <DialogTitle>Redeem — {consumeTarget?.ref_number}</DialogTitle>
            <DialogDescription>
              Pick the pending appointment this credit will be redeemed against. Only appointments matching a service in this package are shown.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">1. Select service</p>
              <div className="space-y-1.5">
                {(consumeTarget?.items || []).filter(it => (it.quantity_total - it.quantity_consumed) > 0).map(it => (
                  <button
                    key={it.service_id}
                    onClick={() => {
                      setConsumeService(it);
                      // Auto-select the first matching appointment — BUT only
                      // when the parent didn't already pin one (e.g. teller is
                      // opening from the payment modal for a specific appt).
                      if (!selectedAppointment) {
                        const first = patientAppts.find(a => a.service_id === it.service_id);
                        setConsumeAppointment(first || null);
                      }
                    }}
                    data-testid={`pkg-consume-pick-${it.service_id}`}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg border transition text-sm",
                      consumeService?.service_id === it.service_id
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-purple-300"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{it.service_name}</span>
                      <span className="text-xs text-gray-500">{it.quantity_total - it.quantity_consumed} left</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {consumeService && (() => {
              // Same OR-match logic as the Use button: prefer service_id, fall
              // back to case-insensitive service name so an appointment booked
              // against an older service_id under the same name still redeems.
              const svcName = String(consumeService.service_name || '').trim().toLowerCase();
              const matching = patientAppts.filter(a =>
                (a.service_id && a.service_id === consumeService.service_id) ||
                (svcName && String(a.service_name || '').trim().toLowerCase() === svcName)
              );
              // Locked mode — parent (Teller modal) has pinned an appointment.
              // Show it as read-only so teller can't accidentally redeem
              // against a different pending appointment.
              if (selectedAppointment) {
                const a = selectedAppointment;
                const svcOk = (a.service_id && a.service_id === consumeService.service_id) ||
                  (svcName && String(a.service_name || '').trim().toLowerCase() === svcName);
                return (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">2. Redeeming against</p>
                    <div
                      className={cn(
                        "w-full px-3 py-2 rounded-lg border text-xs",
                        svcOk ? "border-purple-500 bg-purple-50" : "border-red-300 bg-red-50"
                      )}
                      data-testid="pkg-consume-locked-appt"
                    >
                      <div className="flex items-center justify-between">
                        <span>{a.ref_number || 'Appointment'} · {a.scheduled_date} {a.scheduled_time || ''}</span>
                        <span className="text-[10px] text-gray-500 capitalize">{a.status}</span>
                      </div>
                      {!svcOk && (
                        <div className="mt-1 text-[11px] text-red-700">
                          This appointment&apos;s service doesn&apos;t match the credit — cannot redeem here.
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">2. Redeem against appointment</p>
                  {matching.length === 0 && (
                    <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-1.5" data-testid="pkg-consume-no-appt">
                      No pending appointment for <b>{consumeService.service_name}</b>. Create an appointment first.
                    </p>
                  )}
                  <div className="space-y-1.5">
                    {matching.map(a => (
                      <button
                        key={a.id}
                        onClick={() => setConsumeAppointment(a)}
                        data-testid={`pkg-consume-appt-${a.id}`}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg border transition text-xs",
                          consumeAppointment?.id === a.id
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 hover:border-purple-300"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span>{a.ref_number || 'Appointment'} · {a.scheduled_date} {a.scheduled_time || ''}</span>
                          <span className="text-[10px] text-gray-500 capitalize">{a.status}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsumeOpen(false)}>Cancel</Button>
            <Button
              onClick={submitConsume}
              disabled={consumeSubmitting || !consumeService || !consumeAppointment}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="pkg-consume-submit"
            >
              <Check className="w-4 h-4 mr-2" />
              {consumeSubmitting ? 'Redeeming…' : 'Confirm Redeem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Pay Balance Dialog ============ */}
      <Dialog open={payOpen} onOpenChange={(o) => { if (!o) { setPayOpen(false); setPayTarget(null); } }}>
        <DialogContent className="max-w-sm" data-testid="pkg-pay-dialog">
          <DialogHeader>
            <DialogTitle>Pay balance — {payTarget?.ref_number}</DialogTitle>
            <DialogDescription>Balance: ₹{Number(payTarget?.balance || 0).toLocaleString()}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Amount</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="pl-9" data-testid="pkg-pay-amount" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Payment method</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {paymentMethods.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setPayMethod(m)}
                    data-testid={`pkg-pay-method-${m.name}`}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition",
                      payMethod?.id === m.id
                        ? "bg-orange-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >{m.name}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Credit to (for incentive)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal mt-1" data-testid="pkg-pay-counsellor">
                    {payCounsellor ? payCounsellor.name : <span className="text-gray-500">Pick counsellor…</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput placeholder="Search counsellor..." />
                    <CommandList>
                      <CommandEmpty>No counsellor found.</CommandEmpty>
                      <CommandGroup>
                        {counsellors.map(c => (
                          <CommandItem key={c.id} value={c.name} onSelect={() => setPayCounsellor(c)}>
                            <Check className={cn('mr-2 h-4 w-4', payCounsellor?.id === c.id ? 'opacity-100' : 'opacity-0')} />
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button
              onClick={submitPay}
              disabled={paySubmitting}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="pkg-pay-submit"
            >
              <IndianRupee className="w-4 h-4 mr-2" />
              {paySubmitting ? 'Recording…' : 'Confirm Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Edit Dialog ============ */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setEditTarget(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pkg-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-600" /> Edit — {editTarget?.ref_number}
            </DialogTitle>
            <DialogDescription>
              Paid amount (₹{Number(editTarget?.total_paid || 0).toLocaleString()}) cannot be reduced. Consumed sessions are locked.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-semibold">Services</Label>
              <div className="mt-1 space-y-1.5">
                {editItems.map(it => (
                  <div key={it.service_id} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded px-2 py-1.5" data-testid={`pkg-edit-row-${it.service_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{it.service_name}</p>
                      <p className="text-[10px] text-purple-700">{it.quantity_consumed} consumed{it.quantity_consumed > 0 ? ' (locked)' : ''}</p>
                    </div>
                    <Input
                      type="number"
                      min={it.quantity_consumed}
                      value={it.quantity_total}
                      onChange={(e) => {
                        const q = parseInt(e.target.value, 10);
                        setEditItems(items => items.map(x => x.service_id === it.service_id ? { ...x, quantity_total: isNaN(q) ? 0 : q } : x));
                      }}
                      className="w-16 h-8"
                      data-testid={`pkg-edit-qty-${it.service_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (it.quantity_consumed > 0) {
                          toast.error(`Cannot remove ${it.service_name} — ${it.quantity_consumed} consumed`); return;
                        }
                        setEditItems(items => items.filter(x => x.service_id !== it.service_id));
                      }}
                      disabled={it.quantity_consumed > 0}
                      className="p-1 hover:bg-purple-100 rounded disabled:opacity-30"
                      data-testid={`pkg-edit-remove-${it.service_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-purple-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-2">
              <Label className="text-xs text-gray-500">Add service</Label>
              <div className="flex gap-2 mt-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal h-9" data-testid="pkg-edit-add-service-select">
                      {editAddService ? editAddService.name : <span className="text-gray-500">Pick service…</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search…" />
                      <CommandList>
                        <CommandEmpty>No service found.</CommandEmpty>
                        <CommandGroup>
                          {[...services].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                            <CommandItem key={s.id} value={s.name} onSelect={() => setEditAddService(s)}>
                              <Check className={cn('mr-2 h-4 w-4', editAddService?.id === s.id ? 'opacity-100' : 'opacity-0')} />
                              {s.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Input type="number" min="1" value={editAddQty} onChange={(e) => setEditAddQty(e.target.value)} className="w-16 h-9" data-testid="pkg-edit-add-qty" />
                <Button
                  type="button"
                  onClick={() => {
                    if (!editAddService) { toast.error('Pick a service'); return; }
                    if (editItems.find(x => x.service_id === editAddService.id)) {
                      toast.error('Already in package — edit its qty'); return;
                    }
                    const q = parseInt(editAddQty, 10);
                    if (!(q > 0)) { toast.error('Quantity must be at least 1'); return; }
                    setEditItems([...editItems, { service_id: editAddService.id, service_name: editAddService.name, quantity_total: q, quantity_consumed: 0 }]);
                    setEditAddService(null); setEditAddQty('1');
                  }}
                  className="bg-purple-600 hover:bg-purple-700 h-9"
                  data-testid="pkg-edit-add-button"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Total cost</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="number" value={editTotalCost} onChange={(e) => setEditTotalCost(e.target.value)} className="pl-9 font-semibold" data-testid="pkg-edit-total-cost" />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Already paid: ₹{Number(editTarget?.total_paid || 0).toLocaleString()}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Expiry</Label>
              <Input type="date" value={editExpiry} onChange={(e) => setEditExpiry(e.target.value)} className="mt-1" data-testid="pkg-edit-expiry" />
            </div>

            <div>
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={2} className="mt-1" data-testid="pkg-edit-notes" />
            </div>

            <div>
              <Label className="text-sm font-semibold">Reason for edit <span className="text-gray-400 font-normal">(audit log)</span></Label>
              <Textarea value={editReason} onChange={(e) => setEditReason(e.target.value)} rows={2} placeholder="e.g. Added 1 more session" className="mt-1" data-testid="pkg-edit-reason" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={submitEdit}
              disabled={editSubmitting || editItems.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="pkg-edit-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {editSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ History Dialog ============ */}
      <Dialog open={historyOpen} onOpenChange={(o) => { if (!o) { setHistoryOpen(false); setHistoryTarget(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pkg-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HistoryIcon className="w-5 h-5 text-gray-600" /> History — {historyTarget?.ref_number}
            </DialogTitle>
            <DialogDescription>Previous versions (newest first), each snapshot captured before an edit.</DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {historyLoading && (
              <div className="text-center py-6 text-sm text-gray-500 flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
              </div>
            )}
            {!historyLoading && historyRows.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-500" data-testid="pkg-history-empty">
                No edits recorded for this package yet.
              </div>
            )}
            {!historyLoading && historyRows.length > 0 && (
              <div className="space-y-3">
                {historyRows.map((v, idx) => (
                  <div key={v.id} className="border border-gray-200 rounded-xl p-3 bg-white" data-testid={`pkg-history-row-${idx}`}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div>
                        <p className="text-xs text-gray-500">{String(v.snapshot_at || '').slice(0, 19).replace('T', ' ')}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {v.edited_by_user_name || 'Unknown'} <span className="text-xs text-gray-500 font-normal">({v.edited_by_role || '—'})</span>
                        </p>
                      </div>
                      <Badge className="bg-gray-100 text-gray-700 text-[10px]">v{historyRows.length - idx}</Badge>
                    </div>
                    {v.reason && <p className="text-xs italic text-gray-600 mb-1.5">&ldquo;{v.reason}&rdquo;</p>}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-orange-50/60 border border-orange-100 rounded-lg p-2">
                        <p className="font-semibold text-orange-700 mb-0.5">Before</p>
                        <p className="text-gray-700">Cost: ₹{Number(v.previous?.total_cost || 0).toLocaleString()}</p>
                        <p className="text-gray-700">Paid: ₹{Number(v.previous?.total_paid || 0).toLocaleString()}</p>
                        <div className="mt-1 space-y-0.5">
                          {(v.previous?.items || []).map((it, i) => (
                            <div key={i} className="flex justify-between">
                              <span className="truncate pr-1 text-gray-600">{it.service_name}</span>
                              <span className="text-gray-700">{it.quantity_consumed || 0}/{it.quantity_total}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-emerald-50/60 border border-emerald-100 rounded-lg p-2">
                        <p className="font-semibold text-emerald-700 mb-0.5">After</p>
                        <p className="text-gray-700">Cost: ₹{Number(v.new?.total_cost || 0).toLocaleString()}</p>
                        <p className="text-gray-400">&nbsp;</p>
                        <div className="mt-1 space-y-0.5">
                          {(v.new?.items || []).map((it, i) => (
                            <div key={i} className="flex justify-between">
                              <span className="truncate pr-1 text-gray-600">{it.service_name}</span>
                              <span className="text-gray-700">x{it.quantity_total}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagePOS;
